import { Box, Button, Theme } from "@outschool/backpack";
import { useTranslation } from "@outschool/localization";
import { useTrackEvent } from "@outschool/ui-analytics";
import { useIsMobile, useNavigation } from "@outschool/ui-utils";
import React, { lazy, useCallback, useEffect } from "react";

import { ClassInquiryType } from "../chatProvider";
import { Message } from "../components/Message";
import { Sender } from "../consts";

const ActivityListing = lazy(() =>
  import("@outschool/ui-components-website/src/Listings/MobileListing").then(
    module => ({
      default: module.MobileListing
    })
  )
);

type topRecommendedActivityListingType =
  | ClassInquiryType["topRecommendedActivityListing"]
  | undefined;

export const SendClassRecommendationMessage = ({
  recommendedClass,
  searchPath
}: {
  recommendedClass: topRecommendedActivityListingType;
  searchPath: string | null;
}) => {
  const { t } = useTranslation("client\\components\\chat\\teacherPullChat");

  const trackAdEvent = useTrackEvent();
  const navigate = useNavigation();
  const isMobile = useIsMobile();

  const onClickSeeOtherSuggestedClasses = useCallback(() => {
    trackAdEvent("teacher_pull_see_other_suggested_classes_clicked");

    if (searchPath) {
      navigate(searchPath, { newTab: true, hardNav: true });
    }
  }, [searchPath, navigate, trackAdEvent]);

  const hasTrackedNotFoundEventRef = React.useRef(false);

  useEffect(() => {
    if (!recommendedClass && !hasTrackedNotFoundEventRef.current) {
      trackAdEvent("ai_guide_no_recommended_class_found_message_seen");
      hasTrackedNotFoundEventRef.current = true;
    }
  }, [recommendedClass, trackAdEvent]);

  return (
    <Message
      text={
        !!recommendedClass
          ? t("Here’s a class that could be perfect for your learner.")
          : t(
              "We couldn’t find a class that matches your request. Please try again with different details."
            )
      }
      sender={Sender.System}
    >
      <>
        {!!recommendedClass && (
          <Box
            sx={{
              backgroundColor: "white",
              borderRadius: "8px",
              width: "100%",
              overflow: "hidden",
              boxShadow:
                "0px 0px 4px 0px rgba(0, 0, 0, 0.04), 0px 16px 56px 0px rgba(0, 0, 0, 0.16)",
              flexShrink: 0
            }}
          >
            <ActivityListing
              activity={recommendedClass.activity}
              leader={recommendedClass.leader}
              nextSection={null}
              disabled={false}
              pageLocation="SEARCH_CLASS_INQUIRY"
              openInNewTab={true}
              trackingProperties={{ fromTeacherPullChat: true }}
              isMiniListingCard={isMobile}
              showCTAButton={true}
            />
          </Box>
        )}
        {searchPath && (
          <Button
            size="small"
            variant="outlined"
            sx={(theme: Theme) => ({
              maxHeight: "3.4rem",
              width: "50%",
              [theme.breakpoints.down("md")]: {
                width: "100%"
              }
            })}
            onClick={onClickSeeOtherSuggestedClasses}
          >
            {t("See other suggested classes")}
          </Button>
        )}
      </>
    </Message>
  );
};
